import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Form,
  Input,
  Radio,
  Select,
  Typography,
  Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type {
  DiagnosticRule,
  DiagnosticRuleInput,
  DiagnosticRuleKind,
  DiagnosticRuleOptions,
  DiagnosticRuleStandard,
} from '~/app/services/diagnosticRules/diagnostic-rule.types';
import {
  RULE_DESCRIPTION_MAX,
  RULE_FILE_ACCEPT,
  RULE_TITLE_MAX,
  firmwarePreview,
  firmwareSuggestions,
  modelsForManufacturer,
  validateRuleForm,
} from '../../utils';

const { Text } = Typography;

type FormState = {
  kind: DiagnosticRuleKind;
  title: string;
  description: string;
  alwaysApply: boolean;
  assetModelIds: string[];
  manufacturerOrganizationId?: string;
  standard?: DiagnosticRuleStandard;
  /** As typed. */
  firmwareVersions: string[];
};

function initialState(rule: DiagnosticRule | null): FormState {
  return rule
    ? {
        kind: rule.kind,
        title: rule.title,
        description: rule.description,
        alwaysApply: rule.alwaysApply,
        assetModelIds: rule.assetModels.map((model) => model.id),
        manufacturerOrganizationId:
          rule.manufacturerOrganizationId ?? undefined,
        standard: rule.standard ?? undefined,
        firmwareVersions: [...rule.firmwareVersionsRaw],
      }
    : {
        kind: 'TEXT',
        title: '',
        description: '',
        alwaysApply: false,
        assetModelIds: [],
        firmwareVersions: [],
      };
}

export type RuleFormDrawerProps = {
  open: boolean;
  rule: DiagnosticRule | null;
  options: DiagnosticRuleOptions;
  isSaving: boolean;
  onClose: () => void;
  onSave: (input: DiagnosticRuleInput) => Promise<unknown>;
};

export function RuleFormDrawer({
  open,
  rule,
  options,
  isSaving,
  onClose,
  onSave,
}: RuleFormDrawerProps) {
  const { t } = useTranslation('diagnosticRules');
  const [state, setState] = useState<FormState>(() => initialState(rule));
  const [file, setFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const isEdit = Boolean(rule);

  useEffect(() => {
    if (open) {
      setState(initialState(rule));
      setFile(null);
      setSubmitted(false);
    }
  }, [open, rule]);

  const errors = validateRuleForm(state, file, { requireFile: !isEdit });
  const show = (field: keyof typeof errors) =>
    submitted && errors[field] ? t(errors[field] as string) : undefined;
  const update = (patch: Partial<FormState>) =>
    setState((current) => ({ ...current, ...patch }));
  const preview = firmwarePreview(state.firmwareVersions);
  // Always-apply rules are never ranked: their metadata is not used.
  const metadataDisabled = state.kind === 'TEXT' && state.alwaysApply;
  const modelChoice = useMemo(
    () =>
      modelsForManufacturer(
        options.assetModels,
        state.manufacturerOrganizationId,
      ),
    [options.assetModels, state.manufacturerOrganizationId],
  );
  const suggestions = useMemo(
    () =>
      firmwareSuggestions(
        options.firmwares,
        state.manufacturerOrganizationId,
        state.assetModelIds,
      ),
    [options.firmwares, state.manufacturerOrganizationId, state.assetModelIds],
  );

  const changeManufacturer = (manufacturerOrganizationId?: string) => {
    const offered = new Set(
      modelsForManufacturer(
        options.assetModels,
        manufacturerOrganizationId,
      ).models.map((model) => model.id),
    );
    update({
      manufacturerOrganizationId,
      assetModelIds: state.assetModelIds.filter((id) => offered.has(id)),
    });
  };

  const changeAlwaysApply = (alwaysApply: boolean) =>
    update(
      alwaysApply
        ? {
            alwaysApply,
            assetModelIds: [],
            manufacturerOrganizationId: undefined,
            standard: undefined,
            firmwareVersions: [],
          }
        : { alwaysApply },
    );

  const submit = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;
    await onSave({
      ...state,
      alwaysApply: state.kind === 'TEXT' && state.alwaysApply,
      file: state.kind === 'DOCUMENT' ? file : null,
    });
  };

  return (
    <Drawer
      open={open}
      width={560}
      onClose={onClose}
      destroyOnHidden
      title={isEdit ? t('form.editTitle') : t('form.createTitle')}
      extra={
        <Button type="primary" loading={isSaving} onClick={submit}>
          {isEdit ? t('actions.saveVersion') : t('actions.create')}
        </Button>
      }
    >
      <Form layout="vertical" requiredMark>
        {isEdit && rule ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('form.newVersion', { version: rule.currentVersion + 1 })}
          />
        ) : null}
        <Form.Item label={t('form.kind')}>
          <Radio.Group
            value={state.kind}
            disabled={isEdit}
            onChange={(event) => update({ kind: event.target.value })}
            options={[
              { value: 'TEXT', label: t('kinds.TEXT') },
              { value: 'DOCUMENT', label: t('kinds.DOCUMENT') },
            ]}
          />
        </Form.Item>
        <Form.Item
          label={t('form.title')}
          required
          validateStatus={show('title') ? 'error' : undefined}
          help={show('title')}
        >
          <Input
            value={state.title}
            maxLength={RULE_TITLE_MAX}
            onChange={(event) => update({ title: event.target.value })}
          />
        </Form.Item>
        <Form.Item
          label={
            state.kind === 'TEXT' ? t('form.rule') : t('form.documentAbout')
          }
          required
          validateStatus={show('description') ? 'error' : undefined}
          help={show('description')}
        >
          <Input.TextArea
            value={state.description}
            rows={6}
            showCount
            maxLength={RULE_DESCRIPTION_MAX}
            onChange={(event) => update({ description: event.target.value })}
          />
        </Form.Item>
        {state.kind === 'TEXT' ? (
          <Form.Item extra={t('form.alwaysApplyHelp')}>
            <Checkbox
              checked={state.alwaysApply}
              onChange={(event) => changeAlwaysApply(event.target.checked)}
            >
              {t('form.alwaysApply')}
            </Checkbox>
          </Form.Item>
        ) : (
          <Form.Item
            label={t('form.document')}
            required={!isEdit}
            validateStatus={show('file') ? 'error' : undefined}
            help={
              show('file') ??
              (isEdit && rule?.fileName
                ? t('form.keepDocument', { name: rule.fileName })
                : undefined)
            }
          >
            <Upload.Dragger
              accept={RULE_FILE_ACCEPT}
              maxCount={1}
              beforeUpload={(picked) => {
                setFile(picked);
                return false;
              }}
              onRemove={() => setFile(null)}
              fileList={
                file ? [{ uid: '1', name: file.name, status: 'done' }] : []
              }
            >
              <Flex vertical align="center" gap={8}>
                <InboxOutlined style={{ fontSize: 40 }} />
                <Text strong>{t('form.dropDocument')}</Text>
                <Text type="secondary">{t('form.documentHint')}</Text>
              </Flex>
            </Upload.Dragger>
          </Form.Item>
        )}

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('form.metadataTitle')}
          description={
            metadataDisabled
              ? t('form.metadataDisabled')
              : t('form.metadataHelp')
          }
        />
        <Form.Item label={t('form.manufacturer')}>
          <Select
            allowClear
            showSearch
            disabled={metadataDisabled}
            optionFilterProp="label"
            value={state.manufacturerOrganizationId}
            onChange={changeManufacturer}
            options={options.manufacturers.map((maker) => ({
              value: maker.id,
              label: maker.name,
            }))}
          />
        </Form.Item>
        <Form.Item
          label={t('form.model')}
          validateStatus={show('assetModelIds') ? 'error' : undefined}
          help={
            show('assetModelIds') ??
            (modelChoice.filtered && !metadataDisabled
              ? t('form.modelsOfManufacturer')
              : undefined)
          }
        >
          <Select
            mode="multiple"
            allowClear
            showSearch
            disabled={metadataDisabled}
            optionFilterProp="label"
            placeholder={t('form.modelPlaceholder')}
            value={state.assetModelIds}
            onChange={(assetModelIds: string[]) => update({ assetModelIds })}
            options={modelChoice.models.map((model) => ({
              value: model.id,
              label: model.name,
            }))}
          />
        </Form.Item>
        <Form.Item label={t('form.standard')}>
          <Flex gap={8} align="center">
            <Radio.Group
              disabled={metadataDisabled}
              value={state.standard}
              onChange={(event) => update({ standard: event.target.value })}
              options={[
                { value: 'AC', label: 'AC' },
                { value: 'DC', label: 'DC' },
              ]}
            />
            {state.standard && !metadataDisabled ? (
              <Button
                size="small"
                type="link"
                onClick={() => update({ standard: undefined })}
              >
                {t('actions.clear')}
              </Button>
            ) : null}
          </Flex>
        </Form.Item>
        <Form.Item
          label={t('form.firmware')}
          validateStatus={show('firmwareVersions') ? 'error' : undefined}
          help={
            show('firmwareVersions') ??
            (preview && !metadataDisabled
              ? t('form.firmwareSavedAs', { version: preview })
              : undefined)
          }
        >
          {/* Tags: a typed version is added on Enter; suggestions are the
              firmwares reported by chargers of the chosen manufacturer/models. */}
          <Select
            mode="tags"
            allowClear
            disabled={metadataDisabled}
            tokenSeparators={[',']}
            placeholder={t('form.firmwarePlaceholder')}
            value={state.firmwareVersions}
            onChange={(values: string[]) =>
              update({
                firmwareVersions: values
                  .map((value) => value.trim())
                  .filter(Boolean),
              })
            }
            options={suggestions.map((version) => ({
              value: version,
              label: version,
            }))}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
